/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useQuery } from '@tanstack/react-query';
import { smsTemplateService } from '../../services/sms-template.service';
import SmsTemplateList from '../../pages/partners/sms-template-list';
import WhatsAppTemplateFields, { type WhatsAppBlock } from './whatsapp-template-fields';
import type { UploadedAttachment } from '../../services/email.service';
import { AttachmentField, type AttachmentValue } from './attachment-field';

/**
 * What a notification says on each channel.
 *
 * The field set per channel is the SAME one the live notification master
 * authors — the spec reuses those content shapes unchanged, so an author moving
 * between the two systems is not learning a second vocabulary for the same
 * message.
 *
 * Content is written under `channelTemplates[channel].en`. The stored shape
 * still carries the language key because the dispatcher renders from it and
 * validation requires an `en` variant; what is gone is the UI for authoring any
 * OTHER language, which nothing was using. A translation added later is a
 * change to this component, not to the data already written.
 */
interface ChannelTemplateEditorProps {
  /** The channels declared on the notification — templates are authored for these. */
  channels: string[];
  value: Record<string, any>;
  onChange: (templates: Record<string, any>) => void;
  /** Parameters declared on the notification — offered in the Static mode of
   *  every attachment field, so a template can only reference what exists. */
  parameterNames?: string[];
  /** The project this notification is being authored FOR. The registered SMS
   *  templates are that project's — a send resolves the chosen name against that
   *  project's own gateway account — so the picker must not offer the author's
   *  project's set when the two differ. */
  appId?: string | null;
  testId?: string;
}

type FieldKind = 'text' | 'area' | 'html' | 'media';
interface FieldSpec { name: string; label: string; kind: FieldKind; hint?: string }

/**
 * A working email, offered as the starting point for the HTML body.
 *
 * Authors were pasting HTML from wherever they had it — which arrives wrapped in
 * a markdown fence, and goes out to the recipient as markup they can read. The
 * save now refuses that, so the field has to show what it does want: a whole
 * document, opening at <!DOCTYPE html>, with the placeholders written the same
 * way as every other channel.
 */
const SAMPLE_HTML = `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <style>
      body { margin: 0; background: #f4f5f7; font-family: Arial, Helvetica, sans-serif; }
      .card { max-width: 560px; margin: 24px auto; background: #ffffff;
              border-radius: 8px; padding: 24px; }
      h1 { font-size: 18px; margin: 0 0 12px; color: #111827; }
      p { font-size: 14px; line-height: 1.6; color: #374151; }
      .cta { display: inline-block; margin-top: 16px; padding: 10px 18px;
             background: #2563eb; color: #ffffff; text-decoration: none; border-radius: 6px; }
    </style>
  </head>
  <body>
    <div class="card">
      <h1>{header}</h1>
      <p>Hello {userName}, this happened at {occurredAt}.</p>
      <img src="{imageUrl}" alt="" width="512" style="max-width: 100%; border-radius: 6px;" />
      <a class="cta" href="{actionUrl}">Open in Core Admin</a>
    </div>
  </body>
</html>`;

const HTML_HINT =
  'A whole HTML document, starting at <!DOCTYPE html>. No markdown fences (```) ' +
  'and no <script> — mail clients strip it. Use {placeholders} exactly as on the ' +
  'other channels. Leave this empty to send the plain body in the standard wrapper.';

/**
 * Per channel, the fields an author fills in — mirroring the live master's
 * template schemas rather than inventing a second set.
 */
const FIELDS: Record<string, FieldSpec[]> = {
  inApp: [
    { name: 'title', label: 'Title', kind: 'text' },
    { name: 'body', label: 'Body', kind: 'area' },
    { name: 'icon', label: 'Icon', kind: 'text', hint: 'A PrimeIcons class, e.g. pi pi-bell' },
    { name: 'color', label: 'Colour', kind: 'text' },
  ],
  email: [
    { name: 'subject', label: 'Subject', kind: 'text' },
    { name: 'body', label: 'Body (plain text)', kind: 'area' },
    { name: 'htmlBody', label: 'Body (HTML)', kind: 'html', hint: HTML_HINT },
  ],
  // SMS is not authored here — see the picker below. Only the footer is free
  // text; the name, body and DLT id all come from the registered template,
  // because the wording on the wire must match what the operator approved.
  sms: [
    { name: 'footer', label: 'Footer', kind: 'text' },
  ],
  push: [
    { name: 'title', label: 'Title', kind: 'text' },
    { name: 'body', label: 'Body', kind: 'area' },
    // A URL, a {placeholder} or an upload — the same control WhatsApp uses.
    { name: 'image', label: 'Attachment', kind: 'media' },
    { name: 'icon', label: 'Icon', kind: 'text' },
  ],
  webPush: [
    { name: 'title', label: 'Title', kind: 'text' },
    { name: 'body', label: 'Body', kind: 'area' },
    { name: 'image', label: 'Attachment', kind: 'media' },
    { name: 'url', label: 'Click-through URL', kind: 'text' },
  ],
};

/** Anything not in the table above still gets a title/body pair. */
const FALLBACK_FIELDS: FieldSpec[] = [
  { name: 'title', label: 'Title', kind: 'text' },
  { name: 'body', label: 'Body', kind: 'area' },
];

/** Everything is authored and stored under this one key. */
const LANGUAGE = 'en';

/** One SMS segment. Past it the gateway bills — and may split — a second message. */
const SMS_BODY_LIMIT = 160;

/**
 * Addresses copied on every email of this notification.
 *
 * They sit BESIDE the language variants, not inside one: an address is not a
 * translation, and copying a different office depending on the reader's
 * language is not a thing anybody means. Typed as a comma-separated list
 * because that is how people already write address lists.
 */
const COPY_FIELDS = [
  { name: 'cc', label: 'Cc', hint: 'Copied on every email of this notification, and visible to whoever receives it.' },
  { name: 'bcc', label: 'Bcc', hint: 'Copied without appearing in the message — the address travels in the envelope only.' },
];

const asList = (text: string): string[] =>
  text.split(',').map((a) => a.trim()).filter(Boolean);

/** push / webPush keep one string. `{param}` is a parameter; anything else a URL. */
const stringToAttachment = (held?: string): AttachmentValue => {
  const text = (held || '').trim();
  if (!text) return { kind: 'none' };
  const placeholder = text.match(/^\{([^{}]+)\}$/);
  return placeholder
    ? { kind: 'parameter', name: placeholder[1].trim() }
    : { kind: 'url', url: text };
};

const attachmentToString = (next: AttachmentValue): string => {
  if (next.kind === 'parameter') return `{${next.name}}`;
  if (next.kind === 'url') return next.url;
  // An upload has to become a link here: the field is a url the gateway fetches.
  if (next.kind === 'file') return next.file.url || '';
  return '';
};

const ChannelTemplateEditor = ({
  channels, value, onChange, parameterNames = [], appId = null,
  testId = 'ChannelTemplateEditor',
}: ChannelTemplateEditorProps) => {
  const [activeChannel, setActiveChannel] = useState(0);

  // The registered set, loaded once: it changes only when somebody registers a
  // template, which is a slow, human-reviewed act under DLT.
  const { data: smsTemplates = [], isError: smsFailed, refetch: reloadSms } = useQuery({
    // Keyed on the project: switching the form's project must refetch, not serve
    // the previous project's templates from cache.
    queryKey: ['smsTemplates', 'channelEditor', appId || 'own'],
    queryFn: () => smsTemplateService.list(false, appId),
    staleTime: 3e5,
  });
  // Registering is done here rather than on another screen: an author who has to
  // leave the form to register a template loses the form.
  const [registerOpen, setRegisterOpen] = useState(false);
  const smsError = smsFailed ? 'Could not load registered templates.' : null;
  const smsOptions = smsTemplates.map((t) => ({
    value: t.templateName,
    // An unapproved template is offered but labelled: it exists, and hiding it
    // would leave somebody wondering where the one they registered went.
    label: t.sendable
      ? t.templateName
      : `${t.templateName}  (${t.approvalStatus || 'UNKNOWN'})`,
  }));
  const smsFor = (name?: string) => smsTemplates.find((t) => t.templateName === name);

  // Writes through the language key rather than flattening it away: a
  // notification seeded with other variants keeps them, and the dispatcher's
  // read path is unchanged.
  const setField = (channel: string, field: string, fieldValue: string) =>
    onChange({
      ...value,
      [channel]: {
        ...(value?.[channel] || {}),
        [LANGUAGE]: { ...((value?.[channel] || {})[LANGUAGE] || {}), [field]: fieldValue },
      },
    });

  // Written at the channel level, outside the language key — see COPY_FIELDS.
  const setCopies = (channel: string, field: string, text: string) =>
    onChange({
      ...value,
      [channel]: { ...(value?.[channel] || {}), [field]: asList(text) },
    });

  /**
   * Choosing a template writes the name, the approved wording and the DLT id
   * together.
   *
   * The body is copied rather than left for the author because the text on the
   * wire has to match what the operator approved — a second editable copy here
   * is a divergence waiting to happen, and the gateway rejects the send rather
   * than telling anybody which half was wrong.
   */
  const setSmsTemplate = (name?: string) => {
    const chosen = smsFor(name);
    const variant = { ...((value?.sms || {})[LANGUAGE] || {}) };
    if (!name) {
      delete variant.title; delete variant.body;
      delete variant.templateId; delete variant.dltTemplateId;
    } else {
      variant.title = name;
      variant.body = chosen?.body || '';
      // BOTH ids, and they are not interchangeable. The send prefers
      // `templateId` — the id the GATEWAY issued, which is what its template
      // field expects — and falls back to the OPERATOR's `dltTemplateId`.
      // Writing only the second leaves the send on a fallback path for a
      // template whose gateway id we are holding.
      if (chosen?.providerTemplateId) variant.templateId = chosen.providerTemplateId;
      else delete variant.templateId;
      if (chosen?.dltTemplateId) variant.dltTemplateId = chosen.dltTemplateId;
      else delete variant.dltTemplateId;
    }
    onChange({ ...value, sms: { ...(value?.sms || {}), [LANGUAGE]: variant } });
  };

  // WhatsApp is edited as one block: a change of mode or type rewrites several
  // keys at once, which a per-field setter cannot express.
  /** What this email attaches. Kept in the language variant, which is what the engine
   *  renders and the sender resolves — a descriptor, never the bytes.
   *
   *  Two shapes, because there are two questions. A LIST is the same files on every
   *  send: a policy, a price list. A PLACEHOLDER — `"{documents}"` — is whatever this
   *  fire carries: the order that was just approved, the receipt just generated. The
   *  engine renders a lone placeholder to the value itself, so the list arrives as a
   *  list rather than as text that looks like one.
   */
  // `url` is nullable on an upload (Azure mints one, GridFS does not) and a plain
  // string when somebody pastes a link, so the two have to agree here.
  type Attachment = Partial<UploadedAttachment> & { url?: string | null };

  const attachmentValue = (channel: string): Attachment[] | string | undefined =>
    ((value?.[channel] || {})[LANGUAGE] || {}).attachments;

  const setAttachments = (channel: string, files: Attachment[] | string | null) => {
    const block = { ...((value?.[channel] || {})[LANGUAGE] || {}) };
    // An empty list, or a parameter nobody named, is not an attachment.
    if (typeof files === 'string' ? files.trim() : files?.length) block.attachments = files;
    else delete block.attachments;
    setBlock(channel, block);
  };

  /** The stored email attachment, read back into the shape the field speaks.
   *
   *  A list longer than one can exist from before this form held a single
   *  attachment; the first is shown, and the rest are left untouched until the
   *  author actually changes the field. Silently rewriting them on open would
   *  drop attachments nobody asked to remove. */
  const emailAttachment = (channel: string): AttachmentValue => {
    const held = attachmentValue(channel);
    if (typeof held === 'string') {
      const name = held.replace(/^\{|\}$/g, '').trim();
      return name ? { kind: 'parameter', name } : { kind: 'none' };
    }
    const first = Array.isArray(held) ? held[0] : undefined;
    if (!first) return { kind: 'none' };
    // A row with a fileId came from an upload; one with only a url was pasted.
    return first.fileId
      ? { kind: 'file', file: first as UploadedAttachment }
      : { kind: 'url', url: first.url || '' };
  };

  const setEmailAttachment = (channel: string, next: AttachmentValue) => {
    if (next.kind === 'parameter') { setAttachments(channel, `{${next.name}}`); return; }
    if (next.kind === 'url') { setAttachments(channel, [{ url: next.url }]); return; }
    if (next.kind === 'file') { setAttachments(channel, [next.file]); return; }
    setAttachments(channel, null);
  };

  const setBlock = (channel: string, block: Record<string, any>) =>
    onChange({
      ...value,
      [channel]: { ...(value?.[channel] || {}), [LANGUAGE]: block },
    });

  if (!channels.length) {
    return (
      <Message
        severity="info"
        className="w-full"
        text="Pick a type first — templates are authored for each channel it lists."
      />
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Channel templates</label>
        <span className="block text-xs text-gray-400">
          What this notification says on each channel. Every declared channel needs
          content — a channel listed with none is refused on save.
        </span>
      </div>

      <TabView activeIndex={activeChannel} onTabChange={(e) => setActiveChannel(e.index)}>
        {channels.map((channel) => {
          const fields = FIELDS[channel] || FALLBACK_FIELDS;
          const content = (value?.[channel] || {})[LANGUAGE] || {};
          // Looked up from the registered set rather than read off the stored
          // content: a template can be withdrawn or fall out of approval after
          // this notification named it, and that is worth showing here.
          const selectedSms = channel === 'sms' ? smsFor(content.title) : undefined;

          return (
            <TabPanel key={channel} header={channel}>
              <div className="flex flex-col gap-3">
                {channel === 'sms' && (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Registered template *
                      </label>
                      <Button
                        type="button" link size="small" className="p-0 text-xs"
                        label="Register a new template" icon="pi pi-plus"
                        data-testid={`${testId}.sms.register`}
                        onClick={() => setRegisterOpen(true)}
                      />
                    </div>
                    <Dropdown
                      value={content.title || null}
                      options={smsOptions}
                      optionLabel="label"
                      optionValue="value"
                      filter
                      showClear
                      placeholder={smsTemplates.length
                        ? 'Select a registered template'
                        : 'No templates registered yet'}
                      className="w-full"
                      data-testid={`${testId}.sms.template`}
                      onChange={(e) => setSmsTemplate(e.value)}
                    />
                    {smsError ? (
                      // Not fatal: the stored template name still saves, and
                      // saying so beats an empty list that reads as "none exist".
                      <small className="text-amber-600">{smsError}</small>
                    ) : (
                      <small className="text-xs text-gray-400">
                        Pick the template this notification sends under. Without one the gateway
                        has nothing to send — and on a DLT account an unregistered message is
                        accepted by the API and dropped by the operator, with no error anywhere.
                      </small>
                    )}

                    {selectedSms && !selectedSms.sendable && (
                      <small className="text-xs text-amber-600">
                        {`This template is ${selectedSms.approvalStatus || 'not approved'} — sending under it will be rejected.`}
                      </small>
                    )}
                    {!!selectedSms?.variables?.length && (
                      <small className="text-xs text-gray-400">
                        {`Slots, in order: ${selectedSms.variables.join(', ')}`}
                      </small>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          DLT Template Id
                        </label>
                        <InputText
                          value={content.dltTemplateId || ''}
                          className="w-full font-mono text-xs"
                          data-testid={`${testId}.sms.dltTemplateId`}
                          onChange={(e) => setField('sms', 'dltTemplateId', e.target.value)}
                        />
                        <small className="text-xs text-gray-400">
                          Issued by the telecom operator. Until it is set, the operator drops
                          anything sent under this template.
                        </small>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Gateway Template Id
                        </label>
                        <InputText
                          value={content.templateId || ''}
                          className="w-full font-mono text-xs"
                          data-testid={`${testId}.sms.templateId`}
                          onChange={(e) => setField('sms', 'templateId', e.target.value)}
                        />
                        <small className="text-xs text-gray-400">
                          Used only when there is no DLT id — a non-DLT account never gets one.
                        </small>
                      </div>
                    </div>

                    <div className="flex flex-col gap-1 mt-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {`SMS Body * (Max ${SMS_BODY_LIMIT} characters)`}
                      </label>
                      <InputTextarea
                        value={content.body || ''}
                        rows={4}
                        maxLength={SMS_BODY_LIMIT}
                        className="w-full"
                        data-testid={`${testId}.sms.body`}
                        onChange={(e) => setField('sms', 'body', e.target.value)}
                      />
                      <div className="flex justify-between gap-2">
                        <small className="text-xs text-gray-400">
                          Filled in from the template you pick. Under DLT the wording on the wire
                          is the registered one, so an edit here records what we meant to say —
                          it does not change what the operator sends.
                        </small>
                        <small className={'text-xs shrink-0 '
                          + ((content.body || '').length >= SMS_BODY_LIMIT ? 'text-amber-600' : 'text-gray-400')}>
                          {`${(content.body || '').length}/${SMS_BODY_LIMIT}`}
                        </small>
                      </div>
                    </div>
                  </div>
                )}

                {channel === 'whatsapp' ? (
                  <WhatsAppTemplateFields
                    value={content as WhatsAppBlock}
                    parameterNames={parameterNames}
                    onChange={(next) => setBlock(channel, next)}
                    testId={testId + '.whatsapp'}
                  />
                ) : (
                <div className="grid grid-cols-1 gap-3">
                  {fields.map((field) => (
                    <div key={field.name} className="flex flex-col gap-1">
                      <div className="flex items-center justify-between gap-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{field.label}</label>
                        {field.kind === 'html' && !content[field.name] ? (
                          // Only while the field is empty: an author who has written
                          // their own layout must not be one click from losing it.
                          <Button
                            type="button"
                            link
                            label="Insert example"
                            className="p-0 text-xs"
                            data-testid={`${testId}.${channel}.${field.name}.example`}
                            onClick={() => setField(channel, field.name, SAMPLE_HTML)}
                          />
                        ) : null}
                      </div>
                      {field.kind === 'media' ? (
                        // The label and any field.hint are rendered by the loop
                        // around this, so neither is passed in again. These
                        // channels store a single string, so a file collapses to
                        // the url the gateway can fetch — a fileId means nothing
                        // to something that fetches the image itself.
                        <AttachmentField
                          value={stringToAttachment(content[field.name])}
                          parameterNames={parameterNames}
                          testId={`${testId}.${channel}.${field.name}`}
                          onChange={(next) => setField(channel, field.name, attachmentToString(next))}
                        />
                      ) : field.kind === 'text' ? (
                        <InputText
                          value={content[field.name] || ''}
                          className="w-full"
                          data-testid={`${testId}.${channel}.${field.name}`}
                          onChange={(e) => setField(channel, field.name, e.target.value)}
                        />
                      ) : (
                        <InputTextarea
                          value={content[field.name] || ''}
                          placeholder={field.kind === 'html' ? SAMPLE_HTML : undefined}
                          rows={field.kind === 'html' ? 8 : 4}
                          className={`w-full${field.kind === 'html' ? ' font-mono text-xs' : ''}`}
                          data-testid={`${testId}.${channel}.${field.name}`}
                          onChange={(e) => setField(channel, field.name, e.target.value)}
                        />
                      )}
                      {field.hint ? <small className="text-xs text-gray-400">{field.hint}</small> : null}
                    </div>
                  ))}
                </div>
                )}

                {channel === 'email' && (
                  <div className="grid grid-cols-1 gap-3 border-t border-line pt-3">
                    {COPY_FIELDS.map((field) => (
                      <div key={field.name} className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{field.label}</label>
                        <InputText
                          value={((value?.[channel] || {})[field.name] || []).join(', ')}
                          className="w-full"
                          placeholder="ops@example.com, audit@example.com"
                          data-testid={`${testId}.email.${field.name}`}
                          onChange={(e) => setCopies(channel, field.name, e.target.value)}
                        />
                        <small className="text-xs text-gray-400">{field.hint}</small>
                      </div>
                    ))}
                    <small className="text-xs text-gray-400">
                      Fixed addresses, not an audience — they get a copy but have no preferences
                      and no actions. Anyone on a do-not-contact list is left off.
                    </small>

                    {/* Email asks for an attachment exactly as the other three
                        channels do. What it STORES is its own: a parameter keeps
                        the `"{name}"` string form, a link or an upload the
                        `[{ … }]` list the send API already accepts. */}
                    <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
                      <AttachmentField
                        label="Attachment"
                        value={emailAttachment(channel)}
                        parameterNames={parameterNames}
                        testId={`${testId}.email.attachment`}
                        onChange={(next) => setEmailAttachment(channel, next)}
                      />
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-400">
                  Use <span className="font-mono">{'{parameterName}'}</span> to insert a declared parameter.
                  A placeholder that is not declared is refused on save.
                </p>
              </div>
            </TabPanel>
          );
        })}
      </TabView>

      {/* The registry itself, opened in place. Closing reloads the list, so a
          template registered here is selectable without leaving the form. */}
      <Dialog
        header="SMS templates" visible={registerOpen} style={{ width: '1000px', maxWidth: '96vw' }}
        onHide={() => { setRegisterOpen(false); void reloadSms(); }}
      >
        <SmsTemplateList embedded />
      </Dialog>
    </div>
  );
};

export default ChannelTemplateEditor;
